'use client';

import { useCallback, useRef, useState } from 'react';

import { debounce } from 'lodash';

const useResponsiveHeight = (debounceTime = 50) => {
  const [height, setHeight] = useState<number>(0);
  const isInitialUpdate = useRef(true);

  const debouncedSetHeight = useRef(
    debounce((newHeight: number) => {
      setHeight(newHeight);
      isInitialUpdate.current = true;
    }, debounceTime),
  ).current;

  const updateHeight = useCallback(
    (element: HTMLElement) => {
      const newHeight = element.offsetHeight;
      if (isInitialUpdate.current) {
        setHeight(newHeight);
        isInitialUpdate.current = false;
      } else {
        debouncedSetHeight(newHeight);
      }
    },
    [debouncedSetHeight],
  );

  const resizeObserver = useRef<ResizeObserver>(
    typeof window !== 'undefined'
      ? new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.target instanceof HTMLElement) {
              updateHeight(entry.target);
            }
          }
        })
      : null,
  );

  const observeElement = useCallback((element: HTMLElement | null) => {
    if (element) {
      resizeObserver.current?.observe(element);
      return () => resizeObserver.current?.unobserve(element);
    }
  }, []);

  return { height, observeElement };
};

export { useResponsiveHeight };
